import React, { useCallback, useEffect, useMemo } from 'react';
import { useGame } from '../context/GameContext.jsx';

/**
 * Componente Category para exibir os badges de cartas de investigação (Pessoas, Lugares, Evidências).
 *
 * @param {object} props - As propriedades do componente.
 * @param {'P' | 'L' | 'E'} props.type - O tipo da categoria (Pessoa, Lugar, Evidência).
 */
const Category = ({ type }) => {
  const { currentCards, navigateToCardInCarousel, hypothesisChangedFlag, translations, lang } = useGame();

  let titleKey = '';
  let iconClass = '';
  let headerColorClass = ''; 

  switch (type) {
    case 'P':
      titleKey = 'people_label';
      iconClass = 'fa-solid fa-user';
      headerColorClass = 'text-danger'; 
      break;
    case 'L':
      titleKey = 'places_label';
      iconClass = 'fa-solid fa-map-location-dot';
      headerColorClass = 'text-success'; 
      break;
    case 'E':
      titleKey = 'evidence_label';
      iconClass = 'fa-solid fa-magnifying-glass';
      headerColorClass = 'text-info'; 
      break;
    default:
      titleKey = '';
      iconClass = '';
      headerColorClass = '';
  }

  const translatedTitle = translations[lang]?.[titleKey] || type;

  // Use useMemo para re-calcular os badges apenas quando as dependências relevantes mudarem
  const renderedBadges = useMemo(() => {
    // Create a Set of CardIds for quick lookup of investigated cards
    const investigatedCardIds = new Set(currentCards.map((card) => card.CardId));
    const badges = [];

    for (let i = 1; i <= 12; i++) {
      const cardId = `${type}${String(i)}`; // Construct CardId (e.g., P1, L5, E12)
      const isInvestigated = investigatedCardIds.has(cardId); 
      // Use cardId for localStorage key
      const isHypothesis = localStorage.getItem(`hypothesis_${cardId}`) === 'true'; 

      let badgeClass = '';
      
      if (isHypothesis) {
        badgeClass = 'bg-warning'; // Yellow for bookmarked/hypothesis cards
      } else if (isInvestigated) {
        badgeClass = 'bg-secondary'; // Gray for played/investigated cards
      } else {
        // Original color for uninvestigated cards
        switch(type) {
          case 'P': badgeClass = 'bg-danger'; break;
          case 'L': badgeClass = 'bg-success'; break;
          case 'E': badgeClass = 'bg-info'; break;
          default: badgeClass = 'bg-primary'; 
        }
      }

      badges.push(
        <span
          key={cardId} // Key for React list rendering
          className={`badge ${badgeClass} fs-6 me-1 mb-1 ${isInvestigated ? 'clickable-investigation-badge' : ''}`}
          data-card-code={cardId} // Use cardId for data attribute
          onClick={isInvestigated ? () => navigateToCardInCarousel(cardId) : null}
        >
          {i}
        </span>
      );
    }
    return badges;
  }, [currentCards, navigateToCardInCarousel, hypothesisChangedFlag, type]); // Dependencies for useMemo


  return (
    <div className="col-12 col-md-4">
      <div className="card bg-dark text-light rounded-3 shadow h-100">
        {/* Header title using translated title and color class */}
        <div className={`card-header text-center h5 py-3 ${headerColorClass}`}>
          <i className={`${iconClass} me-2`}></i><span> {translatedTitle}</span>
        </div>
        {/* Card body now renders badges directly */}
        <div className="card-body d-flex flex-wrap gap-1 justify-content-center">
          {renderedBadges} 
        </div>
      </div>
    </div>
  );
};

export default Category;
